import { getAuth } from 'firebase/auth';
import { doc, setDoc, serverTimestamp, getDoc, type DocumentReference } from 'firebase/firestore';
import { db } from '../../repositories/firestoreUtils';
import { logCrossUidAttempt, logImmutableBlocked } from '../logging/secureLogger';

// Structured logs without PII (no raw emails)
function logWrite(event: string, data?: Record<string, unknown>): void {
  try { console.info('auth.write', { event, ...(data || {}) }); } catch {}
}

export class CrossUidWriteError extends Error {
  code = 'CROSS_UID_WRITE_BLOCKED';
}

// Ensure the caller writes only under the currently authenticated UID
export function addUserScopedWriteGuard(targetUid: string, context?: string): void {
  const uid = getAuth().currentUser?.uid || null;
  if (!uid) {
    logWrite('guard_no_auth', { targetUid, context });
    const err = new CrossUidWriteError('No authenticated user for write');
    throw err;
  }
  if (uid !== targetUid) {
    logWrite('guard_uid_mismatch', { currentUid: uid, targetUid, context });
    try { logCrossUidAttempt(uid, targetUid, context); } catch {}
    const err = new CrossUidWriteError('Attempted cross-UID write blocked');
    throw err;
  }
}

export function getCurrentUidOrThrow(): string {
  const uid = getAuth().currentUser?.uid || null;
  if (!uid) throw new CrossUidWriteError('No authenticated user');
  return uid;
}

// Profile helpers that require doc id == current uid
export function userProfileDocForWriteStrict(targetUid: string): DocumentReference {
  addUserScopedWriteGuard(targetUid, 'userProfileDocForWriteStrict');
  return doc(db, 'users', targetUid, 'user_profile', 'user_profile');
}

export async function setUserProfileStrict(targetUid: string, data: any): Promise<void> {
  const ref = userProfileDocForWriteStrict(targetUid);
  // Load existing to enforce immutability of unique identifiers
  const snap = await getDoc(ref);
  const root: any = snap.exists() ? (snap.data() as any) : {};
  const existingFirst: any = Array.isArray(root.profiles) ? (root.profiles[0] || {}) : {};

  // Normalize input structure similar to client mapper
  const incomingProfiles: any[] = Array.isArray(data?.profiles) ? data.profiles : [];
  const incomingFirst: any = incomingProfiles[0] || {};

  // Prepare mutable copy
  const nextFirst: any = { ...(incomingFirst || {}) };

  const existingAccId = (existingFirst?.account_id ?? '') as string;
  const existingMemId = (existingFirst?.member_id ?? '') as string;
  const existingEmail = (existingFirst?.email ?? '') as string;

  const attempted: Array<{ field: string; from: any; to: any }> = [];

  if (existingAccId && String(nextFirst.account_id || '') !== String(existingAccId)) {
    attempted.push({ field: 'account_id', from: existingAccId, to: nextFirst.account_id });
    nextFirst.account_id = existingAccId;
  }
  if (existingMemId && String(nextFirst.member_id || '') !== String(existingMemId)) {
    attempted.push({ field: 'member_id', from: existingMemId, to: nextFirst.member_id });
    nextFirst.member_id = existingMemId;
  }
  if (existingEmail && String(nextFirst.email || '') !== String(existingEmail)) {
    attempted.push({ field: 'email', from: existingEmail, to: nextFirst.email });
    nextFirst.email = existingEmail;
  }

  if (attempted.length > 0) {
    logWrite('immutable_blocked', { uid: targetUid, attempted });
    try { logImmutableBlocked(targetUid, attempted, 'setUserProfileStrict'); } catch {}
  }

  const normalizedProfiles = incomingProfiles.length > 0 ? [
    { ...incomingProfiles[0], ...nextFirst }
  ] : [];

  await setDoc(ref, { ...data, profiles: normalizedProfiles, profile_lastupdated: serverTimestamp() }, { merge: false });
}



